import {
  LitElement,
  html,
} from "https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js";

/**
 * "Ejects" from the shadow DOM.
 * Advantage: The element and it's children will be styled according to (cascading) global style selectors.
 * Disadvantage: slots won't work within these elements.
 */
class LitElementEjected extends LitElement {
  createRenderRoot() {
    // Returning `this` will render the template directly into the light DOM
    return this;
  }
}

export class SubscriptionWidget extends LitElementEjected {
  static properties = {
    productId: { type: String },
    variantId: { type: String },
    products: { type: Object },
    metafields: { type: Object },
    settings: { type: Object },
    _product: { type: Object, state: true },
    _variants: { type: Array, state: true },
    _selectedVariant: { type: Object, state: true },
    _selectedOption: { type: String, state: true },
    _selectedPlan: { type: Object, state: true },
    _plans: { type: Array, state: true },
    _loadingState: { type: String, state: true },
    _quantity: { type: Number, state: true },
    _slashedPrice: { type: String, state: true },
    _actualPrice: { type: String, state: true },
    _productMap: { type: Object, state: true },
  };

  constructor() {
    super();
    this.productId = null;
    this.variantId = null;
    this.formId = null;
    this._product = null;
    this._variants = [];
    this._selectedVariant = null;
    this._selectedPlan = null;
    this._plans = [];
    this._loadingState = "idle";
    this._selectedOption = null;
    this._slashedPrice = undefined;
    this._actualPrice = undefined;
    this._quantity = 1;
    this._productMap = {};
  }

  connectedCallback() {
    super.connectedCallback();
    this.metafields = JSON.parse(this.metafields);
    this._init();
  }

  updated(changedProps) {
    //update pricing when variant id changes
    if (changedProps.has("variantId")) {
      if (
        changedProps.get("variantId") !== undefined &&
        this._loadingState === "idle"
      ) {
        this._setupProductState();
      }
    }
  }

  async _init() {
    if (!this.products || !this.metafields) {
      console.error("recharge widget data missing");
      this._loadingState = "error";
      return;
    }

    this._loadingState = "loading";

    try {
      await this._fetchProductData2();
      this._loadingState = "idle";
    } catch (error) {
      console.error("error initializing widget:", error);
      this._loadingState = "error";
    }
  }

  async _fetchProductData2() {
    const session = await recharge.auth.loginShopifyAppProxy();

    if (this.products.length === 0) throw new Error("No products found");
    const productIds = this.products.map((v) => v.id);

    //grab all the products from recharge
    const productQuery = await recharge.product.productSearch(session, {
      format_version: "2022-06",
      external_product_ids: productIds,
    });

    if (!productQuery.products || productQuery.products.length === 0) {
      throw new Error("product search error");
    }

    //create a map containing product from shopify, product from recharge, the variant and project metaobject
    this._productMap = productQuery.products.reduce((acc, product) => {
      const productId = product.external_product_id;
      const shopifyProduct = this.products.find(
        (p) => p.id.toString() === productId.toString()
      );
      if (!shopifyProduct) return acc;

      const metaobject = this.metafields.find(
        (m) => m.productId.toString() === productId.toString()
      );
      acc[productId] = {
        shopifyProduct,
        metaobject,
        rechargeProduct: product,
        variants: product.variants.filter((v) => v.is_published !== false),
      };
      return acc;
    }, {});

    this._setupProductState2();
  }

  _setupProductState2() {
    this._selectedVariant = Object.values(this._productMap)
      .map((p) => p.variants)
      .flat()
      .find((v) => Number(v.external_variant_id) === Number(this.variantId));

    if (!this._selectedVariant) {
      this._loadingState = "error";
      throw new Error("Selected variant not found");
    }

    //sorting these plans in the order they appear in the metafields
    this._plans = [];
    this.metafields.forEach((meta) => {
      const data = this._productMap[meta.productId];
      if (data.rechargeProduct.plans.length === 0) return;

      this._plans.push(data.rechargeProduct.plans[0]);
    });

    this._selectedPlan = this._plans.length > 0 ? this._plans[0] : null;

    if (this._selectedPlan) {
      this._selectedOption = "subscriptions";
      this._updatePricing();
    } else {
      this._selectedOption = "one-time-purchase";
    }
  }

  _updatePricing() {
    if (!this._selectedVariant || !this._selectedPlan) return;

    for (const plan of this._plans) {
      if (!this._selectedVariant.selling_plan_allocations) continue;

      const allocation = this._selectedVariant.selling_plan_allocations.find(
        (price) =>
          price.selling_plan_id.toString() === plan.external_plan_id.toString()
      );

      if (allocation) {
        plan.actualPrice = `$${allocation.discounted_price}`;
        plan.slashedPrice = `$${this._selectedVariant.prices.unit_price}`;
      } else {
        plan.actualPrice = `$${this._selectedVariant.prices.unit_price}`;
      }
    }

    const sellingPlanInput = document.querySelector(
      'input[type="hidden"][name="selling_plan"]'
    );
    const variantInput = document.querySelector(
      'input[type="hidden"][name="id"]'
    );
    const productInput = document.querySelector(
      `input[type="hidden"][name="product-id"]`
    );
    const addToCartBtn = document.getElementById(
      "sms-main-product-add-to-cart-btn"
    );

    //the problem child
    const selectOptionInput = document.querySelector('select[name="id"].no-js');
    const productJsonElement = document.getElementById(
      "main-product-product-json-script"
    );

    if (!productJsonElement) {
      console.error("Could not find the productJsonElement");
      return null;
    }
    if (!sellingPlanInput) {
      console.error("Could not find the selling_plan input element");
      return null;
    }
    if (!variantInput) {
      console.error("Could not find the variant input element");
      return null;
    }
    if (!productInput) {
      console.error("Could not find the product input element");
      return null;
    }
    if (!selectOptionInput) {
      console.error("Could not find the select option input element");
      return null;
    }

    variantInput.value = this._selectedVariant.external_variant_id;
    productInput.value = this._selectedVariant.external_product_id;
    const option = selectOptionInput.querySelector("option");
    if (option) {
      option.value = this._selectedVariant.external_variant_id; // change the value
    }
    productJsonElement.textContent = JSON.stringify(
      this._productMap[this._selectedVariant.external_product_id].shopifyProduct
    );
    productJsonElement.className = `ProductJson-${this._selectedVariant.external_product_id}`;

    if (this._selectedOption === "subscriptions" && this._selectedPlan) {
      //this price appears to be in cents
      this._actualPrice =
        Number(
          this._productMap[this._selectedVariant.external_product_id].metaobject
            .price_per_month
        ) / 100;
      this._slashedPrice = this._selectedPlan.slashedPrice;
      if (sellingPlanInput)
        sellingPlanInput.value = this._selectedPlan.external_plan_id;
      addToCartBtn.textContent = "Subscribe and Save";
    } else {
      this._actualPrice = `$${this._selectedVariant.prices.unit_price}`;
      this._slashedPrice = undefined;
      if (sellingPlanInput) sellingPlanInput.value = "";
      addToCartBtn.textContent = "Add to Cart";
    }
  }

  _handleSellingPlanChange(event) {
    //update selected variant
    const nextProductId = event.target.getAttribute("data-product-id");
    const nextProduct = this._productMap[nextProductId];

    if (!nextProduct) return;
    this._selectedVariant = nextProduct.variants[0];

    //update selected selling plan
    const selectedPlanId = event.target.value;
    if (selectedPlanId !== "one-time-purchase") {
      this._selectedPlan = this._plans.find(
        (plan) => Number(plan.external_plan_id) === Number(selectedPlanId)
      );
      this._selectedOption = "subscriptions";
    } else {
      this._selectedOption = "one-time-purchase";
    }

    //update actual price
    const actualPrice = this._selectedVariant.prices[0].unit_price;
    const actualPriceWrapper = document.getElementById(
      "sms-main-product-actual-price"
    );
    const actualPriceParent = document.getElementById(
      "sms-main-product-actual-price-parent"
    );
    if (actualPriceWrapper) {
      actualPriceWrapper.textContent = actualPrice;
    }

    //update slashed price
    const slashedPrice = this._selectedVariant.prices[0].compare_at_price;
    const slashedPriceParent = document.createElement("span");

    const slashedPriceWrapper = document.getElementById(
      "sms-main-product-slashed-price"
    );
    if (slashedPriceWrapper) {
      slashedPriceWrapper.textContent = slashedPrice;
    } else {
      //insert the element after the actual price
      const brotherElement = document.getElementById(
        "sms-main-product-actual-price-parent"
      );
      if (slashedPrice) {
        const hiddenSpan = document.createElement("span");
        hiddenSpan.className = "visually-hidden";
        hiddenSpan.setAttribute("data-compare-text", "");
        hiddenSpan.textContent = "Regular price";

        slashedPriceParent.id = "sms-main-product-slashed-price";
        slashedPriceParent.className = "product-price__compare theme-money";
        slashedPriceParent.textContent = slashedPrice;
        brotherElement.insertAdjacentElement("afterend", slashedPriceParent);

        brotherElement.insertAdjacentElement("afterend", hiddenSpan);
      }
    }

    //add class to actual price
    if (Number(slashedPrice) > Number(actualPrice)) {
      actualPriceParent.classList.add("product-price__reduced");
    } else {
      actualPriceParent.classList.remove("product-price__reduced");
    }

    //update product title
    const productTitle = this._productMap[nextProductId].shopifyProduct.title;
    const productTitleWrapper = document.getElementById(
      "sms-main-product-title"
    );
    if (productTitleWrapper) {
      productTitleWrapper.textContent = productTitle;
    }

    this._updatePricing();
    this.requestUpdate();
  }

  _renderOption(option) {
    const plan = this._plans.find(
      (p) =>
        p.external_product_id.ecommerce.toString() ===
        option.productId.toString()
    );
    if (!plan) return;

    const isSelected =
      this._selectedVariant.external_variant_id.toString() ===
      option.variantId.toString();

    return html`
      <div
        class="sms-recharge-widget__radio-option ${isSelected
          ? "sms-rw__radio-selected"
          : "sms-rw__radio-unselected"}"
      >
        <label class="sms-rw__radio-label-container">
          <input
            name="purchase-option"
            id="${plan.id}"
            type="radio"
            .value=${plan.external_plan_id}
            data-interval="${plan.subscription_preferences
              .order_interval_frequency}"
            data-variant-id="${option.variantId}"
            data-product-id="${option.productId}"
            ?checked=${this._selectedPlan && this._selectedPlan.id === plan.id}
            class="sms-rw__radio-option"
            @change=${this._handleSellingPlanChange}
            tabindex="0"
          />
          <div class="sms-rw__radio-label-text">
            <span class="sms-text-uppercase sms-rw__radio-label-text">
              ${plan.subscription_preferences.charge_interval_frequency > 1
                ? "Prepay"
                : "Pay"}
              <br />every
            </span>
            <span
              class="sms-text-uppercase sms-rw__radio-label-text sms-rw__radio-label-title-text"
              >${plan.subscription_preferences.charge_interval_frequency}
              ${plan.subscription_preferences.charge_interval_frequency > 1
                ? "Months"
                : "Month"}</span
            >
          </div>

          <div class="sms-rw__radio-label-text-right">
            <div class="sms-rw__actual-price sms-rw__radio-label-text">
              only $${Number(option.price_per_month) / 100} per box
            </div>
            <div class="sms-rw__radio-label-text">free shipping</div>
          </div>

          <span
            class="sms-rw__save-tag ${isSelected
              ? "sms-rw__radio-selected-tag"
              : ""}"
            >SAVE ${option.discount_in_percentage}%</span
          >
        </label>
      </div>
    `;
  }

  render() {
    if (this._loadingState === "loading") {
      return html`<div class="sms-recharge-widget__loading">Loading...</div>`;
    }

    //get the one time purchase option
    const oneTimeProduct = this.products.find((p) => {
      if (
        p.requires_selling_plan === false &&
        p.selling_plan_groups.length === 0
      ) {
        return true;
      }
    });

    if (
      this._loadingState === "error" ||
      !this._productMap ||
      !this._selectedVariant ||
      !oneTimeProduct
    ) {
      return html`<div class="sms-recharge-widget__error">
        Unable to load subscription options
      </div>`;
    }

    const oneTimeOption = this._productMap[oneTimeProduct.id].metaobject;
    const isOneTimeSelected =
      this._selectedVariant.external_variant_id.toString() ===
      oneTimeOption.variantId.toString();

    return html`
      <div>
        <div class="sms-recharge-widget-header-text">
          ${this.settings.heading_text}
        </div>
        <div class="sms-recharge-widget">
          ${this.metafields.map((metaobject) => this._renderOption(metaobject))}

          <div
            class="sms-recharge-widget__radio-option ${isOneTimeSelected
              ? "sms-rw__radio-selected"
              : "sms-rw__radio-unselected"}"
          >
            <label class="sms-rw__radio-label-container">
              <input
                data-variant-id="${oneTimeOption.variantId}"
                data-product-id="${oneTimeOption.productId}"
                ?checked=${this._selectedVariant.external_variant_id.toString() ===
                oneTimeOption.variantId.toString()}
                class="sms-rw__radio-option"
                tabindex="0"
                type="radio"
                id="${oneTimeOption.productId}-one-time-purchase"
                name="purchase-option"
                value="one-time-purchase"
                @change=${this._handleSellingPlanChange}
              />

              <div class="sms-rw__radio-label-text">
                <span class="sms-text-uppercase sms-rw__radio-label-text">
                  ${this.settings.one_time_purchase_text}
                </span>
              </div>

              <div class="sms-rw__radio-label-text-right">
                <div class="sms-rw__actual-price sms-rw__radio-label-text">
                  only $${Number(oneTimeOption.price_per_month) / 100} per box
                </div>
                <div class="sms-rw__radio-label-text">plus shipping</div>
              </div>
            </label>
          </div>
        </div>

        <!-- <div class="sms-recharge-widget">
          ${this.metafields.map((metaobject) => this._renderOption(metaobject))}

          <div>
            <label class="sms-rw__radio-label-one-time-container">
              <input
                data-variant-id="${oneTimeOption.variantId}"
                data-product-id="${oneTimeOption.productId}"
                ?checked=${this._selectedVariant.external_variant_id.toString() ===
        oneTimeOption.variantId.toString()}
                class="sms-rw__radio-option"
                tabindex="0"
                type="radio"
                id="${oneTimeOption.productId}-one-time-purchase"
                name="purchase-option"
                value="one-time-purchase"
                @change=${this._handleSellingPlanChange}
              />

              <div class="sms-rw__radio-label-one-time-text">
                ${this.settings.one_time_purchase_text}
              </div>
            </label>
          </div>
        </div> -->
      </div>
    `;
  }
}

customElements.define("sms-recharge-widget", SubscriptionWidget);
